// Espace Pro - Layout Principal Unifié
// Dashboard CRM complet pour vendeurs, artisans et professionnels

import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Package, ShoppingCart, Truck, MessageSquare, Ticket, 
  CreditCard, Store, Settings, User, LogOut, ChevronRight,
  Bell, Menu, X, BarChart3, Heart, Clock, Star, HelpCircle,
  Plus, Eye, Edit, Trash2, Search, Filter, Zap, FileText,
  Users, Calendar, Briefcase, FolderOpen, Wrench, PenTool,
  Receipt, TrendingUp, Building2, ClipboardList, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { sellerProfileAPI, sellerStatsAPI } from '../../services/sellerAPI';
import UniversalBanner from '../../components/layout/UniversalBanner';

import { API_URL } from '../../config/api';

// Menu Espace Pro complet
const PRO_MENU = [
  {
    title: 'Tableau de bord',
    items: [
      { icon: BarChart3, label: 'Vue d\'ensemble', path: '/compte', color: '#3B82F6' },
    ]
  },
  {
    title: 'Marketplace',
    items: [
      { icon: Package, label: 'Mes produits', path: '/compte/produits', color: '#10B981' },
      { icon: ShoppingCart, label: 'Commandes reçues', path: '/compte/commandes', color: '#F97316', badge: 'orders' },
      { icon: Zap, label: 'Ventes Flash', path: '/compte/ventes-flash', color: '#EF4444' },
    ]
  },
  {
    title: 'Services Artisan',
    items: [
      { icon: Wrench, label: 'Mes prestations', path: '/compte/prestations', color: '#8B5CF6' },
      { icon: ClipboardList, label: 'Demandes reçues', path: '/compte/demandes', color: '#EC4899' },
      { icon: Calendar, label: 'Planning', path: '/compte/planning', color: '#0EA5E9' },
    ]
  },
  {
    title: 'CRM & Devis',
    items: [
      { icon: Users, label: 'Clients', path: '/compte/clients', color: '#22C55E' },
      { icon: Target, label: 'Prospects', path: '/compte/prospects', color: '#F59E0B' },
      { icon: FileText, label: 'Devis', path: '/compte/devis', color: '#6366F1' },
      { icon: Receipt, label: 'Factures', path: '/compte/factures', color: '#14B8A6' },
    ]
  },
  {
    title: 'Communication',
    items: [
      { icon: MessageSquare, label: 'Messages', path: '/compte/messages', color: '#0EA5E9', badge: 'messages' },
      { icon: Ticket, label: 'Tickets SAV', path: '/compte/tickets', color: '#EC4899', badge: 'tickets' },
    ]
  },
  {
    title: 'Finances',
    items: [
      { icon: CreditCard, label: 'Paiements', path: '/compte/paiements', color: '#22C55E' },
      { icon: TrendingUp, label: 'Rentabilité', path: '/compte/rentabilite', color: '#8B5CF6' },
    ]
  },
  {
    title: 'Ma boutique',
    items: [
      { icon: Store, label: 'Ma boutique', path: '/compte/boutique', color: '#F59E0B' },
      { icon: Star, label: 'Avis clients', path: '/compte/avis', color: '#EAB308' },
      { icon: Briefcase, label: 'Offres d\'emploi', path: '/compte/offres-emploi', color: '#2557a7' },
    ]
  },
];

// Statistiques rapides pour le dashboard
function QuickStats({ stats }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {stats.map((stat, idx) => {
        const Icon = stat.icon;
        return (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white rounded-2xl p-4 border border-gray-100"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}15` }}
              >
                <Icon size={20} style={{ color: stat.color }} />
              </div>
              {stat.trend !== undefined && (
                <span className={`text-xs font-medium ${stat.trend > 0 ? 'text-green-500' : stat.trend < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                  {stat.trend > 0 ? '+' : ''}{stat.trend}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.label}</p>
          </motion.div>
        );
      })}
    </div>
  );
}

// Sidebar Navigation
function Sidebar({ isOpen, onClose, currentPath, seller, badges }) {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const getBadgeValue = (badgeKey) => {
    if (!badges) return null;
    switch (badgeKey) {
      case 'orders': return badges.ordersNew > 0 ? badges.ordersNew : null;
      case 'messages': return badges.unreadMessages > 0 ? badges.unreadMessages : null;
      case 'tickets': return badges.openTickets > 0 ? badges.openTickets : null;
      default: return null;
    }
  };

  return (
    <>
      {/* Overlay mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ x: isOpen ? 0 : '-100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={`fixed top-0 left-0 bottom-0 w-72 bg-white z-50 flex flex-col border-r border-gray-200 lg:relative lg:translate-x-0 lg:z-auto`}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <Building2 size={20} className="text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900">Espace Pro</h1>
                <p className="text-xs text-gray-500">
                  {seller?.shop_name || 'Mon entreprise'}
                </p>
              </div>
            </Link>
            <button onClick={onClose} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Profile Banner */}
        {seller && (
          <div className="px-4 py-3 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
                {seller.shop_name?.charAt(0) || 'P'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{seller.shop_name}</p>
                <p className="text-xs text-gray-500 capitalize">
                  {seller.seller_type === 'both' ? 'Vendeur & Artisan' : 
                   seller.seller_type === 'artisan' ? 'Artisan' : 'Vendeur'}
                </p>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                seller.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {seller.status === 'active' ? 'Actif' : 'En attente'}
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          {PRO_MENU.map((section, sIdx) => (
            <div key={sIdx} className="mb-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item, iIdx) => {
                  const Icon = item.icon;
                  const isActive = currentPath === item.path || 
                    (item.path !== '/compte' && currentPath.startsWith(item.path));
                  const badgeValue = item.badge ? getBadgeValue(item.badge) : null;
                  
                  return (
                    <button
                      key={iIdx}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon size={18} style={{ color: isActive ? 'white' : item.color }} />
                      <span className="flex-1 text-left">{item.label}</span>
                      {badgeValue && (
                        <span className="min-w-[20px] h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center px-1.5">
                          {badgeValue > 99 ? '99+' : badgeValue}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={() => handleNavigation('/compte/parametres')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-all mb-2"
          >
            <Settings size={18} className="text-gray-400" />
            <span>Paramètres</span>
          </button>
          <button
            onClick={() => handleNavigation('/aide')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-all mb-2"
          >
            <HelpCircle size={18} className="text-gray-400" />
            <span>Aide</span>
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('buyer_token');
              localStorage.removeItem('buyer_user');
              localStorage.removeItem('ecosystem_token');
              toast.success('Déconnexion réussie');
              navigate('/');
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all"
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </button>
        </div>
      </motion.aside>
    </>
  );
}

// Header Mobile
function MobileHeader({ onOpenMenu, seller, badges }) {
  const totalBadges = (badges?.ordersNew || 0) + (badges?.unreadMessages || 0) + (badges?.openTickets || 0);
  
  return (
    <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-30">
      <button onClick={onOpenMenu} className="p-2 hover:bg-gray-100 rounded-lg">
        <Menu size={22} />
      </button>
      <h1 className="font-bold text-gray-900 flex-1">Espace Pro</h1>
      <button className="relative p-2 hover:bg-gray-100 rounded-lg">
        <Bell size={22} />
        {totalBadges > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 rounded-full text-[10px] text-white font-bold flex items-center justify-center px-1">
            {totalBadges > 9 ? '9+' : totalBadges}
          </span>
        )}
      </button>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
        {seller?.logo_url ? (
          <img src={seller.logo_url} alt="" className="w-full h-full rounded-full object-cover" />
        ) : (
          <span className="text-white font-bold text-sm">
            {seller?.shop_name?.charAt(0) || 'P'}
          </span>
        )}
      </div>
    </header>
  );
}

// Main Layout Component
export default function CompteLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [seller, setSeller] = useState(null);
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('buyer_token');
        if (!token) {
          // Rediriger vers la connexion
          toast.error('Connecte-toi d\'abord');
          navigate('/connexion');
          return;
        }

        const data = await sellerProfileAPI.get();
        setHasProfile(data.has_profile);
        
        if (data.has_profile && data.seller) {
          setSeller(data.seller);
          
          // Charger les stats pour les badges
          try {
            const stats = await sellerStatsAPI.get();
            setBadges({
              ordersNew: stats.orders?.new || 0,
              unreadMessages: stats.communication?.unread_messages || 0,
              openTickets: stats.communication?.open_tickets || 0
            });
          } catch (e) {
            console.error('Error fetching stats:', e);
          }
        }
      } catch (e) {
        console.error('Error fetching profile:', e);
        if (e.message.includes('401') || e.message.includes('authentifié')) {
          navigate('/connexion');
          return;
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  // Fermer la sidebar sur changement de route (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Chargement de ton espace...</p>
        </div>
      </div>
    );
  }

  // Si pas de profil vendeur, rediriger vers création
  if (!hasProfile && location.pathname !== '/compte/creation') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 size={32} className="text-orange-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Créer ton Espace Pro</h2>
          <p className="text-gray-500 mb-6">
            Configure ton profil professionnel pour accéder à toutes les fonctionnalités
          </p>
          <button
            onClick={() => navigate('/compte/creation')}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-xl transition-colors"
          >
            Commencer la configuration
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full text-gray-500 hover:text-gray-700 py-3 mt-2"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
        seller={seller}
        badges={badges}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <MobileHeader 
          onOpenMenu={() => setSidebarOpen(true)} 
          seller={seller}
          badges={badges}
        />

        {/* Bandeau de Conversion Universel */}
        <UniversalBanner variant="espace-pro" className="py-4" />

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6">
          <Outlet context={{ seller, badges, QuickStats, refreshProfile: () => {} }} />
        </main>
      </div>
    </div>
  );
}
